import Button from "components/Buttons/Button";
import PayoutMethodPreview from "components/PayoutManagement/PayoutMethodPreview";
import Payoutoptions from "components/PayoutManagement/Payoutoptions";
import PaypalForm from "components/PayoutManagement/forms/PaypalForm";
import ZelleForm from "components/PayoutManagement/forms/ZelleForm";
import VenmoForm from "components/PayoutManagement/forms/VenmoForm";
import { PayoutModalPropsType } from "components/PayoutManagement/types";
import { PayoutMethodEnum } from "enum/common";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useLazyPayoutMethodsQuery } from "store/apis/payoutApi";
import { useAppDispatch, useAppSelector } from "hooks/useRedux";
import { RootState } from "store";
import ChequeForm from "components/PayoutManagement/forms/ChequeForm";
import CanadianEFT from "components/PayoutManagement/forms/CanadianEFT";
import { setPayoutMethods } from "store/slices/payoutSlice";
import Note from "components/Note/Note";
import { paratext } from "styles";

interface IInstantPayMethodsProps {
  setLastCompletedStep: (id: number) => void;
}

const PayoutMethods: React.FC<IInstantPayMethodsProps> = ({
  setLastCompletedStep,
}) => {
  const dispatch = useAppDispatch();

  const selectedBusiness = useAppSelector(
    (state: RootState) => state.businesses.business
  );
  const defaultPayoutMethod = useAppSelector(
    (state: RootState) => state.paoyout.payoutMethod
  );

  const isBusinessInCanada =
    selectedBusiness?.address?.countryShortCode === "CA";
  const [
    getPayoutMethods,
    {
      data: payoutMethod,
      isLoading,
      isFetching: isFetchingPayout,
      isSuccess,
      isError,
      error: apiError,
    },
  ] = useLazyPayoutMethodsQuery();

  const [paymentModal, setPaymentModal] = useState<PayoutModalPropsType>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBankForm, setIsBankForm] = useState(false);

  const onHidePaymentModal = () => {
    setPaymentModal(null);
  };

  const renderFooter = useMemo(
    () => (
      <div className="bg-white pt-4  md:items-end items-center md:flex-row flex-col-reverse flex gap-2 w-full">
        {" "}
        {paymentModal?.method === PayoutMethodEnum.CANADA_EFT ? (
          <div className="flex justify-end">
            {!!isBankForm ? (
              <Button
                label={"Go Back"}
                onClick={
                  isBankForm ? () => setIsBankForm(false) : onHidePaymentModal
                }
                variant="secondary"
                customClass="md:w-[168px] w-[100%] !me:0"
                disabled={isSubmitting}
              />
            ) : null}
            <Button
              form={`${paymentModal?.method}-${
                isBankForm ? "bank" : "personal"
              }`}
              label={!isBankForm ? "Next" : "Save Method"}
              type="submit"
              customClass="md:w-[160px] w-[100%] !me:0"
              loading={isSubmitting}
              disabled={isSubmitting}
            />
          </div>
        ) : (
          <div className="flex flex-col w-full gap-4">
            <div>
              <Note note="Make sure your payment method is up-to-date to ensure smooth transactions when you withdraw funds." />
            </div>
            <div className="justify-end flex">
              <Button
                form={paymentModal?.method}
                label={"Save Method"}
                type="submit"
                customClass="md:w-[168px] w-[100%] !me:0 rounded-md "
                loading={isSubmitting}
                disabled={isSubmitting || !paymentModal?.method}
              />
            </div>
          </div>
        )}
      </div>
    ),
    [paymentModal, isBankForm, isSubmitting, paymentModal?.method]
  );

  const fetchPayoutMethods = async () => {
    try {
      await getPayoutMethods({
        type: PayoutMethodEnum.DEFAULT,
        uuid: selectedBusiness?.uuid || "",
      });
    } catch (error) {
      const test: any = apiError;
      toast.error(test?.data?.error);
    } finally {
    }
  };

  const handlePayoutMethodSelection = (method: PayoutMethodEnum) => {
    // @ts-ignore
    setPaymentModal({ ...paymentModal, method });
    if (method === PayoutMethodEnum.CANADA_EFT) {
      setIsBankForm(false);
    }
  };

  const afterSubmit = () => {
    setPaymentModal(null);
    fetchPayoutMethods();
  };

  const renderFormByMethod = (data: PayoutModalPropsType) => {
    switch (data?.method) {
      case PayoutMethodEnum.PAYPAL:
        return (
          <PaypalForm
            data={paymentModal}
            afterSubmit={afterSubmit}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
          />
        );
      case PayoutMethodEnum.VENMO:
        return (
          <VenmoForm
            data={paymentModal}
            afterSubmit={afterSubmit}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
          />
        );
      case PayoutMethodEnum.ZELLE:
        return (
          <ZelleForm
            data={paymentModal}
            afterSubmit={afterSubmit}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
          />
        );
      case PayoutMethodEnum.CANADA_EFT:
        return (
          <CanadianEFT
            data={paymentModal}
            afterSubmit={afterSubmit}
            isBankForm={isBankForm}
            setIsBankForm={setIsBankForm}
            setIsSubmitting={setIsSubmitting}
          />
        );
      case PayoutMethodEnum.CHECK:
        return (
          <ChequeForm
            data={paymentModal}
            afterSubmit={afterSubmit}
            setIsSubmitting={setIsSubmitting}
          />
        );
      default:
        return "Method not found";
    }
  };

  useEffect(() => {
    if (!!isSuccess) {
      dispatch(setPayoutMethods(payoutMethod));
    }
    if (!!isError) {
      dispatch(setPayoutMethods(null));
    }
  }, [isError, isSuccess]);

  useEffect(() => {
    fetchPayoutMethods();
  }, []);

  useEffect(() => {
    if (!!defaultPayoutMethod) {
      setLastCompletedStep(2);
    }
  }, [defaultPayoutMethod]);

  return (
    <div className="flex flex-col gap-2 md:gap-4 w-full border">
      <p className="bg-skyBlue p-3 bg-opacity-5 font-semibold text-xs">
        {!!defaultPayoutMethod ? "Payout Details" : "Enter Payout Details"}
      </p>
      <div className="">
        <div className="w-full my-2 px-4">
          {!!defaultPayoutMethod || isLoading || isFetchingPayout ? (
            <div className="bg-white w-full ">
              <PayoutMethodPreview
                showPopooverOptions={false}
                loading={isLoading || isFetchingPayout}
                defaultPayoutMethod={defaultPayoutMethod}
                setPaymentModal={setPaymentModal}
                setIsBankForm={setIsBankForm}
                afterSubmit={afterSubmit}
              />
            </div>
          ) : (
            <div className="w-full overflow-auto">
              <div className="w-full">
                <p className={`${paratext} !text-xs`}>
                  This is where your money withdrawal will be sent to.
                </p>
                {paymentModal?.type !== "edit" ? (
                  <Payoutoptions
                    onSelect={handlePayoutMethodSelection}
                    paymentModal={paymentModal}
                    isBusinessInCanada={isBusinessInCanada}
                  />
                ) : null}

                {!!paymentModal?.method ? (
                  // Todo: Replace with component(RenderFormByMethod) and make sure state updates as expecte
                  <> {renderFormByMethod(paymentModal)}</>
                ) : null}
                {renderFooter}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayoutMethods;
